import { MenuItem, Stack } from "@mui/material";
import { DownIcon } from "../../../assets";
import { displayDropdownValue } from "../../../helpers/dropdown-helpers";
import { useTagGroup } from "../../../hooks/tags/queries/useTagGroup";
import { DropdownOptions } from "../../../types";
import type { ChangeHandler, Dropdown as TDropdown } from "../types/Modify";
import { Input } from "./Input";

interface Props<D extends object> {
  config: Omit<TDropdown<D>, "controltype">;
  handleChange: ChangeHandler;
}

const loadingValue = "loading";
const loadingState = [
  { label: "Loading Tags...", value: loadingValue, disabled: true },
];

const otherOption = { label: "Other", value: "Other" };

export const Dropdown = <D extends object>({
  config: input,
  handleChange,
}: Props<D>): JSX.Element => {
  const { options: inputOptions = [], tagConfig, other, ...config } = input;

  const [options, loading] = useTagGroup(inputOptions, tagConfig?.tagGroup);

  const values = other
    ? config.value
    : Array.isArray(config.value)
    ? config.value.filter((v) => v !== otherOption.value)
    : config.value;

  const displayOther =
    other && Array.isArray(config.value)
      ? config.value.includes(otherOption.value)
      : config.value === otherOption.value;

  const renderOptions = (dropdownOptions: DropdownOptions) => {
    if (!loading && other) {
      dropdownOptions.push(otherOption);
    }

    return dropdownOptions.map((option, index) => (
      <MenuItem
        key={`${option.value}-${index}`}
        value={option.value}
        disabled={option.disabled}
      >
        {option.label}
      </MenuItem>
    ));
  };

  return (
    <>
      <Stack spacing={0.75}>
        <Input<D>
          config={{
            ...config,
            value: values,
            select: true,
            SelectProps: {
              ...config.SelectProps,
              IconComponent: DownIcon,
              renderValue: (data) => displayDropdownValue(data, options),
            },
            children: renderOptions(loading ? loadingState : options),
          }}
          handleChange={handleChange}
        />

        {displayOther && (
          <Input<D>
            config={{ ...config, value: values, ...other }}
            handleChange={handleChange}
          />
        )}
      </Stack>
    </>
  );
};
